import React, { Component } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';

const INTERNAL_FIELDS = [
  { key: 'medicinename', label: 'Medicine Name', required: true },
  { key: 'packaging', label: 'Packaging', required: true },
  { key: 'mrp', label: 'MRP', required: true },
  { key: 'manufacturer', label: 'Manufacturer', required: true },
  { key: 'batch', label: 'Batch Number', required: true },
  { key: 'quantity', label: 'Quantity', required: true },
  { key: 'expiry', label: 'Expiry Date', required: true },
  { key: 'discount', label: 'Discount', required: false },
  { key: 'vat', label: 'VAT/GST', required: false }
];

class ColumnMappingDialog extends Component {
  constructor(props) {
    super(props);
    
    console.log('[DIALOG-CONSTRUCTOR] ========== INITIALIZING ==========');
    console.log('[DIALOG-CONSTRUCTOR] Props received:', {
      hasExcelHeaders: !!props.excelHeaders,
      excelHeadersLength: props.excelHeaders?.length || 0,
      excelHeadersSample: props.excelHeaders?.slice(0, 3),
      hasSavedMapping: !!props.savedMapping,
      savedMapping: props.savedMapping
    });
    
    const initialMapping = props.savedMapping || this.autoMapColumns(props.excelHeaders);
    console.log('[DIALOG-CONSTRUCTOR] Initial mapping:', initialMapping);
    console.log('[DIALOG-CONSTRUCTOR] Required fields in initial mapping:', {
      medicinename: initialMapping.medicinename || 'NOT SET',
      packaging: initialMapping.packaging || 'NOT SET',
      mrp: initialMapping.mrp || 'NOT SET'
    });
    
    this.state = {
      mapping: initialMapping,
      errors: []
    };
    
    console.log('[DIALOG-CONSTRUCTOR] ========== INITIALIZATION COMPLETE ==========');
  }

  componentDidMount() {
    console.log('[DIALOG] ========== COMPONENT DID MOUNT ==========');
    console.log('[DIALOG] Props:', {
      hasExcelHeaders: !!this.props.excelHeaders,
      excelHeadersLength: this.props.excelHeaders?.length || 0,
      excelHeadersSample: this.props.excelHeaders?.slice(0, 5),
      hasSavedMapping: !!this.props.savedMapping,
      savedMapping: this.props.savedMapping
    });
    
    // Auto-map if no saved mapping and headers available
    if (!this.props.savedMapping && this.props.excelHeaders && this.props.excelHeaders.length > 0) {
      const autoMapping = this.autoMapColumns(this.props.excelHeaders);
      console.log('[DIALOG] Auto-mapping result:', autoMapping);
      console.log('[DIALOG] Auto-mapping has required fields:', {
        medicinename: !!autoMapping.medicinename,
        packaging: !!autoMapping.packaging,
        mrp: !!autoMapping.mrp
      });
      if (Object.keys(autoMapping).length > 0) {
        console.log('[DIALOG] Setting auto-mapped state');
        this.setState({ mapping: autoMapping });
      }
    }
    console.log('[DIALOG] ========== COMPONENT DID MOUNT END ==========');
  }

  componentDidUpdate(prevProps) {
    // Re-map if headers changed (handles async state update)
    if (prevProps.excelHeaders !== this.props.excelHeaders && 
        this.props.excelHeaders && 
        this.props.excelHeaders.length > 0) {
      
      console.log('[DIALOG] ========== HEADERS UPDATED ==========');
      console.log('[DIALOG] Previous headers:', prevProps.excelHeaders);
      console.log('[DIALOG] New headers:', this.props.excelHeaders);
      
      const currentMapping = this.state.mapping;
      const hasMapping = Object.keys(currentMapping).length > 0;
      
      console.log('[DIALOG] Current mapping:', currentMapping);
      console.log('[DIALOG] Has existing mapping:', hasMapping);
      
      // Check if current mapping values exist in new headers
      if (hasMapping) {
        const invalidMappings = Object.keys(currentMapping).filter(key => {
          const mappedValue = currentMapping[key];
          return mappedValue && !this.props.excelHeaders.includes(mappedValue);
        });
        
        if (invalidMappings.length > 0) {
          console.warn('[DIALOG] WARNING: Some mappings are invalid for new headers:', invalidMappings);
          console.warn('[DIALOG] Invalid mapping values:', invalidMappings.map(key => `${key}: "${currentMapping[key]}"`));
          console.log('[DIALOG] Re-running auto-mapping due to invalid mappings');
          
          // Clear invalid mappings and re-run auto-map
          const autoMapping = this.autoMapColumns(this.props.excelHeaders);
          console.log('[DIALOG] New auto-mapping:', autoMapping);
          this.setState({ mapping: autoMapping, errors: [] });
        }
      } else {
        // No mapping exists, run auto-map
        const autoMapping = this.autoMapColumns(this.props.excelHeaders);
        console.log('[DIALOG] New auto-mapping:', autoMapping);
        if (Object.keys(autoMapping).length > 0) {
          console.log('[DIALOG] Updating state with auto-mapping');
          this.setState({ mapping: autoMapping });
        }
      }
      console.log('[DIALOG] ========== HEADERS UPDATE END ==========');
    }
    
    // Update mapping if savedMapping prop changed, but validate it first
    if (prevProps.savedMapping !== this.props.savedMapping && this.props.savedMapping) {
      console.log('[DIALOG] Saved mapping changed:', this.props.savedMapping);
      
      // Validate that saved mapping values exist in current headers
      if (this.props.excelHeaders && this.props.excelHeaders.length > 0) {
        const invalidSavedMappings = Object.keys(this.props.savedMapping).filter(key => {
          const mappedValue = this.props.savedMapping[key];
          return mappedValue && !this.props.excelHeaders.includes(mappedValue);
        });
        
        if (invalidSavedMappings.length > 0) {
          console.warn('[DIALOG] WARNING: Saved mapping has invalid values:', invalidSavedMappings);
          console.log('[DIALOG] Running auto-mapping instead of using saved mapping');
          const autoMapping = this.autoMapColumns(this.props.excelHeaders);
          this.setState({ mapping: autoMapping, errors: [] });
        } else {
          console.log('[DIALOG] Saved mapping is valid, using it');
          this.setState({ mapping: this.props.savedMapping });
        }
      } else {
        this.setState({ mapping: this.props.savedMapping });
      }
    }
  }

  autoMapColumns = (headers) => {
    if (!headers || headers.length === 0) return {};
    
    const mapping = {};
    
    console.log('[AUTO-MAP] ========== AUTO-MAPPING START ==========');
    console.log('[AUTO-MAP] Excel headers (original):', headers);
    
    // Normalize headers: lowercase, trim, remove numbers, underscores, extra spaces
    // BUT keep track of which normalized header maps to which original header
    const normalizedToOriginal = {};
    const headerLower = headers.map((h, idx) => {
      const normalized = String(h)
        .toLowerCase()
        .trim()
        .replace(/^\d+_/, '')  // Remove leading numbers and underscore (e.g., "2_product_name" -> "product_name")
        .replace(/[_\s]+/g, ''); // Remove underscores and spaces (e.g., "product_name" -> "productname")
      
      normalizedToOriginal[idx] = {
        original: h,
        normalized: normalized
      };
      
      return normalized;
    });
    
    console.log('[AUTO-MAP] Normalized headers:', headerLower);
    console.log('[AUTO-MAP] Normalization mapping:', normalizedToOriginal);
    
    // Define mapping patterns for automatic detection - CRITICAL: use 'medicinename' as key
    const patterns = {
      'medicinename': ['medicinename', 'productname', 'medicine', 'product', 'drug', 'drugname', 'itemname', 'item', 'name'],
      'packaging': ['packaging', 'pack', 'packing', 'packagetype', 'packtype', 'packsize', 'package'],
      'mrp': ['mrp', 'price', 'sellingprice', 'rate', 'amount', 'cost'],
      'manufacturer': ['manufacturer', 'mfg', 'company', 'brand', 'mfr', 'supplier', 'suppliername'],
      'batch': ['batch', 'batchno', 'batchnumber', 'lotno', 'lot'],
      'quantity': ['quantity', 'qty', 'stock', 'quantityonhand', 'available', 'availablequantity', 'qtyloose', 'availablequanityloose'],
      'expiry': ['expiry', 'expirydate', 'expdate', 'exp', 'expirationdate'],
      'discount': ['discount', 'disc', 'discountpercent', 'specialprice', 'specialpricevalue'],
      'vat': ['vat', 'gst', 'tax', 'igst', 'cgst', 'sgst']
    };
    
    // Try to match each internal field with excel headers
    Object.keys(patterns).forEach(internalField => {
      const patterns_list = patterns[internalField];
      
      // Find the first header whose normalized version matches any pattern
      let matchedIndex = -1;
      for (let i = 0; i < headerLower.length; i++) {
        const normalizedHeader = headerLower[i];
        const isMatch = patterns_list.some(pattern => 
          normalizedHeader === pattern || 
          normalizedHeader.includes(pattern) || 
          pattern.includes(normalizedHeader)
        );
        
        if (isMatch) {
          matchedIndex = i;
          break;
        }
      }
      
      if (matchedIndex >= 0) {
        // CRITICAL: Use the ORIGINAL Excel header name, not the normalized one
        const originalHeader = normalizedToOriginal[matchedIndex].original;
        mapping[internalField] = originalHeader;
        console.log(`[AUTO-MAP] ✓ Matched: "${internalField}" <- Excel column "${originalHeader}" (normalized: "${headerLower[matchedIndex]}")`);
      } else {
        console.warn(`[AUTO-MAP] ✗ No match found for: "${internalField}"`);
      }
    });
    
    console.log('[AUTO-MAP] ========== AUTO-MAPPING END ==========');
    console.log('[AUTO-MAP] Final mapping:', mapping);
    console.log('[AUTO-MAP] Required fields check:', {
      medicinename: mapping.medicinename || 'NOT MAPPED',
      packaging: mapping.packaging || 'NOT MAPPED',
      mrp: mapping.mrp || 'NOT MAPPED'
    });
    
    return mapping;
  };

  componentDidUpdate(prevProps) {
    if (prevProps.savedMapping !== this.props.savedMapping && this.props.savedMapping) {
      this.setState({ mapping: this.props.savedMapping });
    }
  }

  handleMappingChange = (internalField, excelColumn) => {
    this.setState(prevState => ({
      mapping: {
        ...prevState.mapping,
        [internalField]: excelColumn
      },
      errors: []
    }));
  };

  validateMapping = () => {
    const { mapping } = this.state;
    const { excelHeaders } = this.props;
    const errors = [];

    // Check only required fields
    const requiredFields = INTERNAL_FIELDS.filter(f => f.required === true);
    
    console.log('[VALIDATION] ========== VALIDATING MAPPING ==========');
    console.log('[VALIDATION] Required fields:', requiredFields.map(f => f.label));
    console.log('[VALIDATION] Current mapping:', mapping);
    console.log('[VALIDATION] Excel headers:', excelHeaders);
    
    requiredFields.forEach(field => {
      const mappedValue = mapping[field.key];
      
      // Check if field is mapped
      if (!mappedValue || mappedValue === '') {
        errors.push(`${field.label} is not mapped`);
        console.error(`[VALIDATION] ERROR: ${field.label} is not mapped`);
      } 
      // Check if mapped value exists in Excel headers
      else if (!excelHeaders || !excelHeaders.includes(mappedValue)) {
        errors.push(`${field.label} is mapped to "${mappedValue}" which doesn't exist in Excel headers`);
        console.error(`[VALIDATION] ERROR: ${field.label} mapped to "${mappedValue}" not found in headers:`, excelHeaders);
      }
      else {
        console.log(`[VALIDATION] ✓ ${field.label} correctly mapped to "${mappedValue}"`);
      }
    });

    console.log('[VALIDATION] Validation errors:', errors);
    console.log('[VALIDATION] ========== VALIDATION END ==========');
    return errors;
  };

  handleSave = () => {
    const errors = this.validateMapping();
    
    console.log('[DIALOG-SAVE] ========= HANDLE SAVE DEBUG =========');
    console.log('[DIALOG-SAVE] Current mapping state:', this.state.mapping);
    console.log('[DIALOG-SAVE] Excel headers:', this.props.excelHeaders);
    console.log('[DIALOG-SAVE] Validation errors:', errors);
    
    // Validate that mapped columns are not empty strings
    const requiredFields = ['medicinename', 'packaging', 'mrp', 'manufacturer', 'batch', 'quantity', 'expiry'];
    const emptyMappings = requiredFields.filter(field => {
      const value = this.state.mapping[field];
      return !value || String(value).trim() === '';
    });
    
    if (emptyMappings.length > 0) {
      console.error('[DIALOG-SAVE] ERROR: Empty column mappings for:', emptyMappings);
      const missingLabels = emptyMappings.map(key => {
        const field = INTERNAL_FIELDS.find(f => f.key === key);
        return field ? field.label : key;
      }).join(', ');
      toast.error(`Please select Excel columns for: ${missingLabels}`);
      this.setState({ errors: [`Missing required mappings: ${missingLabels}`] });
      return;
    }
    
    // Verify columns exist in Excel headers
    const invalidColumns = requiredFields.filter(field => {
      const excelColumn = this.state.mapping[field];
      return excelColumn && !this.props.excelHeaders.includes(excelColumn);
    });
    
    if (invalidColumns.length > 0) {
      const invalidDetails = invalidColumns.map(field => {
        const mappedValue = this.state.mapping[field];
        const fieldObj = INTERNAL_FIELDS.find(f => f.key === field);
        return `${fieldObj?.label || field}: "${mappedValue}"`;
      }).join(', ');
      console.error('[DIALOG-SAVE] ERROR: Mapped columns not found in Excel:', invalidDetails);
      toast.error(`Some mapped columns don't exist in your Excel file: ${invalidDetails}`);
      this.setState({ errors: [`Invalid mappings: ${invalidDetails}`] });
      return;
    }
    
    console.log('[DIALOG-SAVE] ========= HANDLE SAVE DEBUG END =========');
    
    if (errors.length > 0) {
      this.setState({ errors });
      toast.error('Please fix all mapping errors before continuing');
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Clear errors and proceed
    this.setState({ errors: [] });
    console.log('[DIALOG-SAVE] ✓ Validation passed! Calling props.onSave with mapping:', this.state.mapping);
    this.props.onSave(this.state.mapping);
    this.props.onClose();
  };

  handleSkip = () => {
    // Use existing mapping or create default mapping
    if (this.props.savedMapping) {
      this.props.onSave(this.props.savedMapping);
    }
    this.props.onClose();
  };

  render() {
    const { show, onClose, excelHeaders } = this.props;
    const { mapping, errors } = this.state;

    console.log('[DIALOG-RENDER] ========== RENDER START ==========');
    console.log('[DIALOG-RENDER] Excel headers:', excelHeaders);
    console.log('[DIALOG-RENDER] Current mapping:', mapping);

    // Safety check for excelHeaders
    if (!excelHeaders || excelHeaders.length === 0) {
      console.error('[DIALOG-RENDER] ERROR: No Excel headers available!');
      return (
        <Modal show={show} onHide={onClose} size="lg" backdrop="static" keyboard={false}>
          <Modal.Header closeButton>
            <Modal.Title>📋 Map Your Excel Columns (Required)</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">
              <strong>❌ Error:</strong> No Excel headers detected. Please upload a valid Excel file first.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </Modal.Footer>
        </Modal>
      );
    }

    // Check if all required fields are mapped AND the mapped values exist in excelHeaders
    const requiredFields = INTERNAL_FIELDS.filter(f => f.required);
    
    // Validate each required field
    const fieldValidation = requiredFields.map(field => {
      const mappedValue = mapping[field.key];
      const hasValue = mappedValue && mappedValue !== '';
      const existsInHeaders = hasValue && excelHeaders && excelHeaders.includes(mappedValue);
      
      return {
        field: field.key,
        label: field.label,
        hasValue,
        mappedValue,
        existsInHeaders,
        isValid: hasValue && existsInHeaders
      };
    });
    
    console.log('[DIALOG-RENDER] Field validation:', fieldValidation);
    
    const allRequiredMapped = fieldValidation.every(v => v.isValid);
    const missingRequiredFields = fieldValidation.filter(v => !v.isValid).map(v => v.label);
    
    console.log('[DIALOG-RENDER] All required mapped:', allRequiredMapped);
    console.log('[DIALOG-RENDER] Missing fields:', missingRequiredFields);
    console.log('[DIALOG-RENDER] ========== RENDER END ==========');

    // Helper: Find column index with fuzzy matching (same as parent component)
    const findColumnIndex = (headers, columnName) => {
      if (!columnName || !headers) return -1;
      
      // Try exact match first
      let index = headers.indexOf(columnName);
      if (index >= 0) return index;
      
      // Try case-insensitive and trimmed match
      const normalizedSearch = String(columnName).toLowerCase().trim();
      return headers.findIndex(h => 
        String(h).toLowerCase().trim() === normalizedSearch
      );
    };

    // Generate preview of first row with mapping
    const excelData = this.props.excelRawData || [];
    const previewData = excelData.length > 0 ? excelData[0] : null;
    const mappingPreview = {};
    
    if (previewData && excelHeaders) {
      INTERNAL_FIELDS.forEach(field => {
        const excelColumn = mapping[field.key];
        if (excelColumn) {
          const columnIndex = findColumnIndex(excelHeaders, excelColumn);
          if (columnIndex >= 0 && columnIndex < previewData.length) {
            const value = previewData[columnIndex];
            mappingPreview[field.key] = value !== undefined && value !== null ? String(value).substring(0, 30) : '(empty)';
          } else if (columnIndex < 0) {
            console.warn(`[PREVIEW-WARNING] Column "${excelColumn}" not found in headers for field "${field.key}"`);
            mappingPreview[field.key] = '(column not found)';
          }
        }
      });
      
      // Debug preview generation
      console.log('[PREVIEW] Mapping preview generated:', mappingPreview);
      console.log('[PREVIEW] Required fields:', {
        medicinename: mappingPreview.medicinename,
        packaging: mappingPreview.packaging,
        mrp: mappingPreview.mrp
      });
    }

    return (
      <Modal show={show} onHide={onClose} size="lg" backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>📋 Map Your Excel Columns (Required)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.length > 0 && (
            <Alert variant="danger">
              <strong>❌ Cannot Proceed - Missing Required Mappings:</strong>
              <ul className="mb-0 mt-2">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
          
          {!allRequiredMapped && errors.length === 0 && (
            <Alert variant="warning">
              <strong>⚠️ Action Needed:</strong> Please map all required fields to continue.
              <div className="mt-2">
                <strong>Missing:</strong> {missingRequiredFields.join(', ')}
              </div>
            </Alert>
          )}
          
          {/* <Alert variant="info" className="mb-3">
            <strong>📥 Your Excel Headers Can Be Anything!</strong>
            <ul className="mb-0 mt-2" style={{fontSize: '0.95em'}}>
              <li>Your Excel file can have <strong>any column names</strong> (Product, Drug, Item Name, Price, etc.)</li>
              <li><strong style={{color: '#dc3545'}}>MANDATORY FIELDS (Required):</strong>
                <ul style={{marginTop: '8px', marginBottom: '8px'}}>
                  <li>Medicine Name</li>
                  <li>Packaging</li>
                  <li>MRP</li>
                  <li>Manufacturer</li>
                  <li>Batch Number</li>
                  <li>Quantity</li>
                  <li>Expiry Date</li>
                  <li>GST / VAT</li>
                </ul>
              </li>
              <li>Select which Excel column corresponds to each required field below</li>
              <li>All other fields are optional</li>
            </ul>
          </Alert> */}

          <Row>
            {INTERNAL_FIELDS.map(field => {
              const mappedValue = mapping[field.key];
              const hasMapping = mappedValue && mappedValue !== '';
              const isValidMapping = hasMapping && excelHeaders && excelHeaders.includes(mappedValue);
              const isInvalidMapping = hasMapping && !isValidMapping;
              
              return (
              <Col md={6} key={field.key} className="mb-3">
                <Form.Group>
                  <Form.Label style={{fontWeight: field.required ? 'bold' : 'normal', fontSize: field.required ? '15px' : '14px'}}>
                    {field.required && <span style={{color: '#dc3545', fontSize: '18px', marginRight: '5px'}}>•</span>}
                    {field.label}
                    {field.required && <span className="text-danger" style={{fontWeight: 'bold'}}> * </span>}
                  </Form.Label>
                  <Form.Control
                    as="select"
                    value={isValidMapping ? mappedValue : ''}
                    onChange={(e) => this.handleMappingChange(field.key, e.target.value)}
                    className={field.required && (!hasMapping || isInvalidMapping) ? 'border border-danger' : ''}
                    style={{
                      borderWidth: field.required && (!hasMapping || isInvalidMapping) ? '2px' : '1px',
                      borderColor: field.required && (!hasMapping || isInvalidMapping) ? '#dc3545' : undefined,
                      backgroundColor: field.required && (!hasMapping || isInvalidMapping) ? '#fff3cd' : undefined
                    }}
                  >
                    <option value="">-- Select Your Excel Column --</option>
                    {excelHeaders && excelHeaders.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </Form.Control>
                  {field.required && !hasMapping && (
                    <small style={{color: '#dc3545', fontWeight: '600', display: 'block', marginTop: '5px'}}>
                      ⚠️ This field must be mapped to continue
                    </small>
                  )}
                  {/* {isInvalidMapping && (
                    <small style={{color: '#dc3545', fontWeight: '600', display: 'block', marginTop: '5px'}}>
                      ❌ Mapped value "{mappedValue}" not found in Excel columns. Please select a valid column.
                    </small>
                  )} */}
                  {isValidMapping && (
                    <small style={{color: '#28a745', fontWeight: '600', display: 'block', marginTop: '5px'}}>
                      ✓ Mapped to: {mappedValue}
                    </small>
                  )}
                </Form.Group>
              </Col>
              );
            })}
          </Row>

          <Alert variant={allRequiredMapped ? 'success' : 'warning'} className="mt-4">
            <strong>📊 Mapping Status:</strong>
            <div style={{fontSize: '0.9em', marginTop: '10px'}}>
              {fieldValidation.map(validation => (
                <div key={validation.field} style={{
                  color: validation.isValid ? '#28a745' : '#dc3545',
                  fontWeight: '600',
                  marginBottom: '5px'
                }}>
                  {validation.isValid ? '✓' : '❌'} <strong>{validation.label}:</strong>{' '}
                  {validation.hasValue 
                    ? (validation.existsInHeaders 
                        ? validation.mappedValue 
                        : `"${validation.mappedValue}" (NOT FOUND IN EXCEL)`)
                    : '⚠️ NOT MAPPED (Required)'}
                </div>
              ))}
            </div>
          </Alert>

          {/* Preview of first row with mapping */}
          {previewData && Object.keys(mappingPreview).length > 0 && (
            <Alert variant="info" className="mt-3">
              <strong>🔍 Preview - First Row Values:</strong>
              <div style={{fontSize: '0.85em', marginTop: '10px', fontFamily: 'monospace'}}>
                {INTERNAL_FIELDS.filter(f => mapping[f.key]).map(field => (
                  <div key={field.key} style={{marginBottom: '5px'}}>
                    <strong style={{color: field.required ? '#dc3545' : '#000'}}>
                      {field.label}:
                    </strong>{' '}
                    <span style={{
                      backgroundColor: mappingPreview[field.key] === '(empty)' ? '#fff3cd' : '#d4edda',
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {mappingPreview[field.key] || '(empty)'}
                    </span>
                  </div>
                ))}
              </div>
              {INTERNAL_FIELDS.filter(f => f.required && (!mapping[f.key] || mappingPreview[f.key] === '(empty)')).length > 0 && (
                <div style={{color: '#dc3545', marginTop: '10px', fontSize: '0.9em'}}>
                  ⚠️ Warning: Some required fields have empty values in the first row
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div style={{width: '100%'}}>
            {/* {!allRequiredMapped && (
              <Alert variant="danger" className="mb-2 py-2" style={{fontSize: '0.9em'}}>
                <strong>❌ Cannot Continue:</strong> Please map all 5 mandatory fields (Medicine Name, Packaging, MRP, Manufacturer, Batch Number) to proceed.
              </Alert>
            )} */}
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                variant={allRequiredMapped ? "success" : "secondary"} 
                onClick={this.handleSave}
                disabled={!allRequiredMapped}
                style={{
                  cursor: !allRequiredMapped ? 'not-allowed' : 'pointer',
                  opacity: !allRequiredMapped ? 0.6 : 1
                }}
              >
                {allRequiredMapped ? '✓ Verify & Continue' : '⚠️ Complete Required Mapping First'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ColumnMappingDialog;
